import { ApolloClient, useApolloClient } from '@apollo/client';
import { subscribe as subscribeBase, TConnectionItem } from '@chocolate-soup-inc/cs-api-consumer-utils';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { onSubscriptionData } from '../../config/apollo/cache';
import {
  GetEventDocument,
  OnCompanyEventChangedDocument,
  TGetEventQuery,
  TGetEventQueryVariables,
  TListClientOneOffEventsQuery,
  TListClientOneOffEventsQueryVariables,
  TListCompanyUpcomingEventsQuery,
  TListCompanyUpcomingEventsQueryVariables,
  TOnCompanyEventChangedSubscription,
  TOnCompanyEventChangedSubscriptionVariables,
  useListClientOneOffEventsLazyQuery,
  useListCompanyUpcomingEventsLazyQuery
} from '../../generated/graphql';
import { useQueryAll } from '../shared/useQueryAll';

type TOnEventSubscriptionDataProps = {
  client: ApolloClient<object>;
  data?: TOnCompanyEventChangedSubscription;
  vars?: TOnCompanyEventChangedSubscriptionVariables;
};

const onEventSubscriptionData = async (props: TOnEventSubscriptionDataProps) => {
  const { client, data, vars } = props;

  const { companyId, id, _deleted } = data?.onCompanyEventChanged || {};

  if (_deleted) return onSubscriptionData(data as Record<string, any>, vars);
  if (!companyId || !id) return;

  const response = await client.query<TGetEventQuery, TGetEventQueryVariables>({
    query: GetEventDocument,
    variables: {
      id,
      companyId
    }
  });

  const eventData = response.data.getEvent as TConnectionItem;

  if (eventData) {
    const subscriptionData = {
      onCompanyEventChanged: eventData
    };

    // UPDATE QUERIES THAT DO NOT DEPEND ONLY ON COMPANYID
    onSubscriptionData(subscriptionData, {
      companyId
    });
  }
};

export type TClientOneOffEvent = Exclude<TListClientOneOffEventsQuery['listClientOneOffEvents']['items'][number], null | undefined>;

export const useQueryAllClientOneOffEvents = (variables: TListClientOneOffEventsQueryVariables) => {
  const { data, error, loading, refetch } = useQueryAll<TListClientOneOffEventsQuery, TListClientOneOffEventsQueryVariables>({
    fetchPolicy: 'network-only',
    useQuery: useListClientOneOffEventsLazyQuery,
    variables
  });

  const events = useMemo(() => {
    return _.compact(data?.listClientOneOffEvents.items || []);
  }, [data?.listClientOneOffEvents.items]);

  return {
    data: events,
    error,
    loading: data == null || loading,
    refetch
  };
};

export const useSubscribeToCompanyEventChangedSubscription = (variables: TOnCompanyEventChangedSubscriptionVariables) => {
  const client = useApolloClient();

  useEffect(() => {
    subscribeBase({
      client,
      query: OnCompanyEventChangedDocument,
      variables,
      onSubscriptionData: async (data, vars) => {
        await onEventSubscriptionData({
          client,
          data: data as TOnCompanyEventChangedSubscription,
          vars: vars as TOnCompanyEventChangedSubscriptionVariables
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, JSON.stringify(variables)]);
};

export const useQueryAllCompanyUpcomingEvents = (variables: TListCompanyUpcomingEventsQueryVariables) => {
  useSubscribeToCompanyEventChangedSubscription({
    companyId: variables.companyId
  });

  const { data, error, loading } = useQueryAll<TListCompanyUpcomingEventsQuery, TListCompanyUpcomingEventsQueryVariables>({
    useQuery: useListCompanyUpcomingEventsLazyQuery,
    variables
  });

  const upcomingEvents = useMemo(() => {
    return _.compact(data?.listCompanyUpcomingEvents.items || []);
  }, [data?.listCompanyUpcomingEvents.items]);

  return {
    data: upcomingEvents,
    error,
    loading
  };
};
